import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import SubscriptionService from "services/SubscriptionService";

const getErrorMessage = (error) =>
  error?.response?.data?.message || error?.message || "Internal server error";

export const fetchSubscriptions = createAsyncThunk(
  "subscriptions/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await SubscriptionService.getSubscriptions(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchSubscriptionById = createAsyncThunk(
  "subscriptions/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await SubscriptionService.getSubscriptionById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const createSubscriptionPack = createAsyncThunk(
  "subscriptions/create",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SubscriptionService.createSubscription(data);
      return response.data?.subscription;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const updateSubscriptionPack = createAsyncThunk(
  "subscriptions/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await SubscriptionService.updateSubscription(id, data);
      return response.data?.subscription;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const deleteSubscriptionPack = createAsyncThunk(
  "subscriptions/delete",
  async (id, { rejectWithValue }) => {
    try {
      await SubscriptionService.deleteSubscription(id);
      return id;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const updateSubscriptionPackStatus = createAsyncThunk(
  "subscriptions/updateStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await SubscriptionService.updateSubscriptionStatus(
        id,
        status
      );
      return response.data?.subscription;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchEligibleSubscriptionFormations = createAsyncThunk(
  "subscriptions/fetchEligibleFormations",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SubscriptionService.getEligibleFormations();
      return response.data?.data || [];
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const initialState = {
  list: [],
  page: 1,
  pages: 1,
  totalItems: 0,
  limit: 10,
  loading: false,
  saving: false,
  error: null,
  current: null,
  eligibleFormations: [],
};

const subscriptionsSlice = createSlice({
  name: "subscriptions",
  initialState,
  reducers: {
    setCurrentSubscription: (state, { payload }) => {
      state.current = payload;
    },
    resetCurrentSubscription: (state) => {
      state.current = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSubscriptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubscriptions.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.list = payload?.data || [];
        state.page = payload?.page || 1;
        state.pages = payload?.pages || 1;
        state.limit = payload?.limit || 10;
        state.totalItems = payload?.totalItems || 0;
      })
      .addCase(fetchSubscriptions.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      .addCase(fetchSubscriptionById.fulfilled, (state, { payload }) => {
        state.current = payload;
      })
      .addCase(fetchSubscriptionById.rejected, (state, { payload }) => {
        state.error = payload;
      })
      .addCase(createSubscriptionPack.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createSubscriptionPack.fulfilled, (state, { payload }) => {
        state.saving = false;
        if (payload) {
          state.list = [payload, ...state.list];
          state.totalItems += 1;
        }
      })
      .addCase(createSubscriptionPack.rejected, (state, { payload }) => {
        state.saving = false;
        state.error = payload;
      })
      .addCase(updateSubscriptionPack.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateSubscriptionPack.fulfilled, (state, { payload }) => {
        state.saving = false;
        if (payload) {
          state.list = state.list.map((item) =>
            item?._id === payload?._id ? payload : item
          );
          if (state.current?._id === payload?._id) {
            state.current = payload;
          }
        }
      })
      .addCase(updateSubscriptionPack.rejected, (state, { payload }) => {
        state.saving = false;
        state.error = payload;
      })
      .addCase(deleteSubscriptionPack.fulfilled, (state, { payload }) => {
        if (payload) {
          state.list = state.list.filter((item) => item?._id !== payload);
          state.totalItems = Math.max(state.totalItems - 1, 0);
          if (state.current?._id === payload) {
            state.current = null;
          }
        }
      })
      .addCase(deleteSubscriptionPack.rejected, (state, { payload }) => {
        state.error = payload;
      })
      .addCase(updateSubscriptionPackStatus.fulfilled, (state, { payload }) => {
        if (payload) {
          state.list = state.list.map((item) =>
            item?._id === payload?._id ? payload : item
          );
          if (state.current?._id === payload?._id) {
            state.current = payload;
          }
        }
      })
      .addCase(updateSubscriptionPackStatus.rejected, (state, { payload }) => {
        state.error = payload;
      })
      .addCase(fetchEligibleSubscriptionFormations.fulfilled, (state, { payload }) => {
        state.eligibleFormations = payload || [];
      })
      .addCase(fetchEligibleSubscriptionFormations.rejected, (state, { payload }) => {
        state.error = payload;
      });
  },
});

export const { setCurrentSubscription, resetCurrentSubscription } =
  subscriptionsSlice.actions;

export default subscriptionsSlice.reducer;
